// Fund daily-accruals sub-route — /fund/:fundId/accruals (16.d.4.3).
// Third step of the 16.d.4 mega-page split. The per-investor daily fee-accrual
// ledger with a MGMT/PERF toggle. Self-loads via GET
// /ops/funds/{id}/accruals?kind=&limit=50 using fundId from the URL; also
// fetches the fund (GET /ops/funds) for its base_currency + name, since the
// accruals endpoint doesn't carry currency. No shared fund context yet (the
// FundLayout + subnav lands in 16.d.4.5). Manager-gated in App.tsx.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PageHeader } from '../../components/shell';
import { fmtBase, fmtUnitPriceOps } from '../../lib/format';
import { api } from '../../lib/api';

const fmtUnits = (s: string | number) =>
  Number(s).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 8 });

export default function FundAccruals() {
  const { fundId } = useParams<{ fundId: string }>();
  const [funds, setFunds] = useState<any[] | null>(null);
  const [accrualKind, setAccrualKind] = useState<'management' | 'performance'>('management');
  const [accruals, setAccruals] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const fund = useMemo(() => funds?.find(f => f.fund_id === fundId) || null, [funds, fundId]);

  useEffect(() => {
    api<any[]>('/ops/funds').then(setFunds).catch(() => setErr('Failed to load fund.'));
  }, []);

  useEffect(() => {
    if (!fundId) return;
    setAccruals(null);
    api<any>(`/ops/funds/${fundId}/accruals?kind=${accrualKind}&limit=50`)
      .then(setAccruals)
      .catch(() => setAccruals(null));
  }, [fundId, accrualKind]);

  if (!fundId) return null;

  return (
    <>
      <PageHeader
        kicker="FUND"
        title={<>Daily <em>accruals.</em></>}
        meta={fund ? <span className="label-s">{fund.name}</span> : undefined}
      />
      {err && <p className="serif-body" style={{ color: 'var(--oxblood)', marginTop: 12 }}>{err}</p>}

      <section style={{ marginTop: 24 }}>
        <div className="kicker-row">
          <span className="kicker">DAILY ACCRUALS</span>
          <span style={{ marginLeft: 16 }}>
            <button
              className="label-s"
              onClick={() => setAccrualKind('management')}
              style={{ marginRight: 12, color: accrualKind === 'management' ? 'var(--ink)' : 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer' }}
            >MGMT</button>
            <button
              className="label-s"
              onClick={() => setAccrualKind('performance')}
              style={{ color: accrualKind === 'performance' ? 'var(--ink)' : 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer' }}
            >PERF</button>
          </span>
        </div>
        {accruals && accruals.rows && accruals.rows.length > 0 ? (
          <table className="tbl" style={{ marginTop: 12 }}>
            <thead>
              {accrualKind === 'management' ? (
                <tr><th>DATE</th><th>INVESTOR</th><th data-align="right">BASIS</th><th data-align="right">RATE</th><th data-align="right">ACCRUED</th></tr>
              ) : (
                <tr><th>DATE</th><th>INVESTOR</th><th data-align="right">UNITS</th><th data-align="right">NAV / UNIT</th><th data-align="right">HWM</th><th data-align="right">ACCRUED</th></tr>
              )}
            </thead>
            <tbody>
              {accruals.rows.map((row: any) => accrualKind === 'management' ? (
                <tr key={row.id}>
                  <td className="num" style={{ fontSize: 12 }}>{row.accrual_date}</td>
                  <td>{row.investor_name}</td>
                  <td data-align="right" className="num">{fmtBase(Number(row.basis_base), fund?.base_currency)}</td>
                  <td data-align="right" className="num">{row.rate_annual}%</td>
                  <td data-align="right" className="num">{fmtBase(Number(row.accrued_amount_base), fund?.base_currency)}</td>
                </tr>
              ) : (
                <tr key={row.id}>
                  <td className="num" style={{ fontSize: 12 }}>{row.accrual_date}</td>
                  <td>{row.investor_name}</td>
                  <td data-align="right" className="num">{fmtUnits(row.units_held)}</td>
                  <td data-align="right" className="num">{fmtUnitPriceOps(Number(row.nav_per_unit_base), fund?.base_currency)}</td>
                  <td data-align="right" className="num">{fmtUnitPriceOps(Number(row.hwm_per_unit_base), fund?.base_currency)}</td>
                  <td data-align="right" className="num">{fmtBase(Number(row.accrued_amount_base), fund?.base_currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="label-s" style={{ marginTop: 12, color: 'var(--ink-3)' }}>
            NO {accrualKind === 'management' ? 'MGMT-FEE' : 'PERFORMANCE-FEE'} ACCRUALS YET.
          </p>
        )}
        {accruals && accruals.total > accruals.rows.length && (
          <p className="label-s" style={{ marginTop: 6, color: 'var(--ink-3)' }}>
            SHOWING {accruals.rows.length} OF {accruals.total} · OLDER ROWS PAGINATE
          </p>
        )}
      </section>
    </>
  );
}
